/**
 * SignInModal (Iter 9.47.o)
 *
 * Popup version of the LoginPage entry step. Uses `useAdminCopy` for hero
 * title + subtitle so admins control the wording from one place; legal
 * links (Terms / Privacy / Beehiiv TOU / Beehiiv Privacy) read from the
 * same admin copy table and stay in sync with the standalone /login page.
 *
 *  - Email magic link (PRIMARY, /api/auth/send-magic-link)
 *  - Google (Emergent-managed)
 *  - Phone (returning users) - Sinch SMS OTP wires in next iter
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhygitalIcon from "@/components/PhygitalIcon";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function SignInModal({ open, onOpenChange, redirectUrl = "/me" }) {
  const c = useAdminCopy();
  const [loginMethod, setLoginMethod] = useState("email"); // "email" | "phone"
  const [step, setStep] = useState("entry"); // "entry" | "email-sent" | "otp"
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkDebug, setMagicLinkDebug] = useState(null);

  const reset = () => {
    setLoginMethod("email");
    setStep("entry");
    setEmail("");
    setPhone("");
    setOtpCode("");
    setVerificationId(null);
    setMaskedPhone("");
    setMagicLinkDebug(null);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-magic-link`, {
        email: email.trim().toLowerCase(),
        redirect_url: redirectUrl,
      });
      if (res.data?.success) {
        setStep("email-sent");
        toast.success("Magic link sent! Check your email.");
        if (res.data.debug_link) setMagicLinkDebug(res.data.debug_link);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: do not hardcode URL or add fallbacks (breaks Emergent auth).
    const redirect = window.location.origin + redirectUrl;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const e164 = "+1" + phone.replace(/\D/g, "");
      const res = await axios.post(`${API}/auth/phone/send-otp`, { phone_number: e164 });
      setVerificationId(res.data.verification_id);
      setMaskedPhone(res.data.masked_phone);
      setStep("otp");
      toast.success(`Code sent to ${res.data.masked_phone}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send code.";
      if (err.response?.status === 503) {
        toast.error("Phone sign-in is not configured yet. Use email magic link.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (loading || otpCode.length !== 6) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/phone/verify-otp`, {
        verification_id: verificationId,
        otp_code: otpCode,
      });
      localStorage.setItem("session_token", res.data.session_token);
      toast.success(res.data.is_new_user ? "Welcome to Phygital!" : "Welcome back!");
      window.location.href = redirectUrl;
    } catch (err) {
      const msg = err.response?.data?.detail || "Verification failed.";
      toast.error(msg);
      setOtpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm p-8" data-testid="sign-in-modal">
        {step !== "entry" && (
          <button
            onClick={() => setStep("entry")}
            className="absolute top-4 left-4 flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
            data-testid="sign-in-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}

        {/* Logo */}
        <div className="flex justify-center mb-2">
          <PhygitalIcon className="w-12 h-12" variant="dark" />
        </div>

        {step === "entry" && loginMethod === "email" && (
          <>
            <div className="text-center mb-4">
              <h1
                className="text-2xl font-bold text-gray-900 mb-1 tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="sign-in-modal-title"
              >
                {c("login_page.hero_title", "Welcome to Phygital")}
              </h1>
              <p className="text-sm text-gray-500" data-testid="sign-in-modal-subtitle">
                {c("login_page.hero_subtitle", "Your preferences. Your experience.")}
              </p>
            </div>

            <form onSubmit={handleSendMagicLink} className="space-y-3" data-testid="sign-in-email-form">
              <div>
                <Label htmlFor="signin-email" className="text-xs font-medium text-gray-700">
                  Email address
                </Label>
                <Input
                  id="signin-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 mt-1 rounded-full border-gray-200 bg-gray-50 px-5 text-sm focus:border-gray-400 focus:ring-gray-400"
                  data-testid="sign-in-email-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email.includes("@")}
                className="w-full h-12 rounded-full bg-black hover:bg-gray-800 text-white font-medium"
                data-testid="sign-in-email-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending link...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue with Email
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <Button
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full h-12 rounded-full border-gray-200 hover:bg-gray-50 font-medium"
              data-testid="sign-in-google-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <div className="mt-5 text-center">
              <button
                onClick={() => setLoginMethod("phone")}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
                data-testid="sign-in-switch-to-phone-btn"
              >
                <Phone className="w-3 h-3" />
                Returning user? Sign in with phone
              </button>
            </div>

            {/* Legal links - admin-controlled via useAdminCopy. URLs and labels
                are sourced from /api/admin-copy/legal_links so Naomi can edit
                Terms / Privacy / Beehiiv TOU / Beehiiv Privacy in one place. */}
            <p
              className="text-center text-[10px] text-gray-400 mt-6 whitespace-nowrap overflow-hidden text-ellipsis"
              data-testid="sign-in-legal-links"
            >
              {c("legal_links.prefix", "By continuing, you agree to")}{" "}
              <Link to="/terms" className="underline hover:text-gray-700">
                {c("legal_links.terms_label", "Terms")}
              </Link>,{" "}
              <Link to="/privacy" className="underline hover:text-gray-700">
                {c("legal_links.privacy_label", "Privacy")}
              </Link>,{" "}
              <a
                href={c("legal_links.beehiiv_tou_url", "https://www.beehiiv.com/tou")}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-gray-700"
              >
                {c("legal_links.beehiiv_tou_label", "Beehiiv TOU")}
              </a>{" "}
              &{" "}
              <a
                href={c("legal_links.beehiiv_privacy_url", "https://www.beehiiv.com/privacy")}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-gray-700"
              >
                {c("legal_links.beehiiv_privacy_label", "Beehiiv Privacy")}
              </a>
            </p>
          </>
        )}

        {step === "entry" && loginMethod === "phone" && (
          <>
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-[11px] text-gray-600 uppercase tracking-wider mb-3">
                <Phone className="w-3 h-3" /> Returning Users Only
              </div>
              <h1
                className="text-2xl font-bold text-gray-900 mb-1 tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Sign in with phone
              </h1>
              <p className="text-sm text-gray-500">
                You must already have a Phygital account with a linked phone number.{" "}
                <button
                  onClick={() => setLoginMethod("email")}
                  className="text-gray-900 underline hover:no-underline"
                  data-testid="sign-in-switch-to-email-btn"
                >
                  New here? Use email
                </button>
              </p>
            </div>

            <form onSubmit={handlePhoneSubmit} className="space-y-3" data-testid="sign-in-phone-form">
              <div>
                <Label htmlFor="signin-phone" className="text-xs font-medium text-gray-700">
                  Phone number
                </Label>
                <Input
                  id="signin-phone"
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  className="h-12 mt-1 rounded-full border-gray-200 bg-gray-50 px-5 text-sm focus:border-gray-400 focus:ring-gray-400"
                  maxLength={14}
                  data-testid="sign-in-phone-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={phone.replace(/\D/g, "").length < 10}
                className="w-full h-12 rounded-full bg-black hover:bg-gray-800 text-white font-medium"
                data-testid="sign-in-phone-submit"
              >
                Send code
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </>
        )}

        {step === "otp" && (
          <>
            <div className="text-center mb-4">
              <h1
                className="text-2xl font-bold text-gray-900 mb-1 tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="sign-in-modal-otp-title"
              >
                Enter your code
              </h1>
              <p className="text-sm text-gray-500">
                We texted a verification code to <span className="font-medium text-gray-700">{maskedPhone}</span>
              </p>
            </div>
            <form onSubmit={handleVerifyOtp} className="space-y-3" data-testid="sign-in-otp-form">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="Enter code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength="6"
                autoFocus
                className="text-center text-2xl font-mono tracking-[0.4em]"
                data-testid="sign-in-otp-input"
              />
              <Button
                type="submit"
                className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                disabled={loading || otpCode.length < 4}
                data-testid="sign-in-otp-verify-btn"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <button
                type="button"
                onClick={() => { setStep("entry"); setOtpCode(""); }}
                className="text-xs text-gray-500 hover:text-gray-900 w-full text-center"
                data-testid="sign-in-otp-change-number-btn"
              >
                Change phone number
              </button>
            </form>
          </>
        )}

        {step === "email-sent" && (
          <>
            <div className="text-center">
              <h1
                className="text-2xl font-bold text-gray-900 mb-2 tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="sign-in-modal-sent-title"
              >
                Check your inbox
              </h1>
              <p className="text-sm text-gray-500 mb-4">
                We sent a magic link to{" "}
                <span className="font-medium text-gray-700">{email}</span>. Click it to finish
                signing in.
              </p>
              {magicLinkDebug && (
                <a
                  href={magicLinkDebug}
                  className="text-xs text-amber-700 underline break-all bg-amber-50 border border-amber-200 rounded p-2 block"
                  data-testid="sign-in-dev-magic-link"
                >
                  {magicLinkDebug}
                </a>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
